import React from 'react';
import { FieldValues, UseFormWatch } from 'react-hook-form';

import { Button, Guideline, Select, SpinnerOverlay } from '@components/common';

import {
	useQueryPortalMilestonesGroupedTasksToLink,
} from '@hooks/portal/milestones/useQueryPortalMilestonesGroupedTasksToLink';

import { portalMilestones as portalMilestonesSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setAssociatedTask } from '@slices/portalMilestones';

import { isEmpty } from '@utils/common';
import { parseStringToDateInMilliseconds } from '@utils/kpi-utils/date';
import { getTasksToLinkSelectOptions } from '@components/ModalComponents/MilestoneForm/Dependencies/utils';
import { checkIsTaskInMilestoneTimeRange } from '@components/ModalComponents/MilestoneForm/utils';

import { IPortalTaskToLinkDTO } from '@interfaces/portal/dto/taskManagementDTO';

import colors from '@constants/colors';
import { associatedTasksOutsideRangeGuideline, associatedTasksOutsideRangeHeader } from './constants';
import { milestoneFormFields, tabsNames } from '@components/ModalComponents/MilestoneForm/constants';

import AssociatedTaskItem from './AssociatedTaskItem';
import AssociatedTasksSelectOption from './AssociatedTasksSelectOption';

import styles from './Dependencies.module.scss';

const GUIDELINE_ICON_SIZE = '12';

interface IDependenciesProps {
	isEditMode: boolean;
	isViewMode: boolean;
	milestoneId?: string;
	projectId?: string;
	setCurrentTab: (value: string) => void;
	onSubmit: () => void;
	watch: UseFormWatch<FieldValues>;
}

const Dependencies = ({
	milestoneId,
	projectId,
	setCurrentTab,
	onSubmit,
	watch,
	isEditMode,
	isViewMode,
}: IDependenciesProps) => {
	const dispatch = useAppDispatch();

	const { associatedTasks } = useAppSelector(portalMilestonesSelector);

	const milestoneStartDate = watch(milestoneFormFields.startDate);

	const milestoneEndDate = watch(milestoneFormFields.endDate);

	const selectedProjectId = (isEditMode || isViewMode) ? projectId : watch(milestoneFormFields.project)?.value;

	const {
		portalMilestonesGroupedTasksToLink,
		isLoadingPortalMilestonesGroupedTasksToLink,
	} = useQueryPortalMilestonesGroupedTasksToLink({
		projectId: selectedProjectId,
		milestoneId,
	});

	const tasksToLinkSelectOptions = getTasksToLinkSelectOptions(portalMilestonesGroupedTasksToLink, associatedTasks);

	const onAssociatedTasksChange = ({ id, taskId, title, endDate, startDate }: IPortalTaskToLinkDTO) => {
		dispatch(setAssociatedTask({
			associatedTask: {
				id,
				taskId,
				title,
				startDate,
				endDate,
			},
		}));
	};

	const onClickBackHandler = () => {
		setCurrentTab(tabsNames.details);
	};

	const associatedTasksInTimeRange = associatedTasks.filter(associatedTask => {
		return checkIsTaskInMilestoneTimeRange({
			associatedTaskStartDate: associatedTask?.startDate
				? parseStringToDateInMilliseconds(associatedTask.startDate) : undefined,
			associatedTaskEndDate: associatedTask?.endDate
				? parseStringToDateInMilliseconds(associatedTask.endDate) : undefined,
			milestoneStartDate,
			milestoneEndDate,
		});
	});

	const associatedTasksOutsideTimeRange = associatedTasks.filter(associatedTask => {
		return !checkIsTaskInMilestoneTimeRange({
			associatedTaskStartDate: associatedTask?.startDate
				? parseStringToDateInMilliseconds(associatedTask.startDate) : undefined,
			associatedTaskEndDate: associatedTask?.endDate
				? parseStringToDateInMilliseconds(associatedTask.endDate) : undefined,
			milestoneStartDate,
			milestoneEndDate,
		});
	});

	return (
		<>
			{isLoadingPortalMilestonesGroupedTasksToLink && <SpinnerOverlay />}
			<div className={styles.dependenciesContainer}>
				<div className={styles.associatedTasksInput}>
					<Select
						customComponents={{
							Option: (props) => AssociatedTasksSelectOption({
								...props,
							}),
						}}
						disabled={isViewMode}
						handleChange={onAssociatedTasksChange}
						isWithoutUseForms
						label='Associated tasks'
						maxMenuHeight={240}
						name='associatedTasks'
						options={tasksToLinkSelectOptions}
						placeholder='Choose task'
						value={null}
					/>
				</div>
				<div className={styles.associatedTasksList}>
					<>
						{associatedTasksInTimeRange.map(associatedTask => {
							return (
								<AssociatedTaskItem
									key={associatedTask.id}
									associatedTask={associatedTask}
									isViewMode={isViewMode}
								/>
							);
						})}
					</>
					{!isEmpty(associatedTasksOutsideTimeRange)
						&& <div className={styles.associatedTasksOutsideRangeList}>
							<div className={styles.titleContainer}>
								<div className={styles.title}>
									{associatedTasksOutsideRangeHeader}
								</div>
								<Guideline
									guidelineText={associatedTasksOutsideRangeGuideline}
									iconColor={colors.gray40}
									iconHeight={GUIDELINE_ICON_SIZE}
									iconWidth={GUIDELINE_ICON_SIZE}
									placement='right'
								/>
							</div>
							{associatedTasksOutsideTimeRange.map(associatedTask => {
								return (
									<AssociatedTaskItem
										key={associatedTask.id}
										associatedTask={associatedTask}
										isViewMode={isViewMode}
									/>
								);
							})}
						</div>
					}
				</div>
			</div>
			{!isEditMode && !isViewMode
				&& <div className={styles.buttons}>
					<Button
						onClick={onClickBackHandler}
						text='Back'
						withoutBackground
					/>
					<Button
						onClick={onSubmit}
						text='Save'
					/>
				</div>
			}
		</>
	);
};

export default Dependencies;
